import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Form, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import { getCardData, getCardError, getDataLoading, getVisibleFormData } from '../../selectors'
import { useDebounce } from 'src/react-app/hooks/useDebounce/useDebounce'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'
import { isEqual } from 'lodash'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'

interface ClaimWorkFormsProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const Forms: React.FC<ClaimWorkFormsProps> = memo((props: ClaimWorkFormsProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const claimWorkFormData = useSelector(getCardData)
  const claimWorkFormIsLoading = useSelector(getDataLoading)
  const claimWorkFormError = useSelector(getCardError)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()
  const formRef = useRef<typeof visibleFormData>()
  const [prevFormData, setPrevFormData] = useState<any>(undefined)
  const [initialFilled, setInitialFilled] = useState<boolean>(false)

  useEffect(() => {
    if (visibleFormData && claimWorkFormData) {
      // Обновляем formRef если данные изменились или это первая загрузка
      if (!formRef.current || !isEqual(formRef.current?.resultObject, visibleFormData.resultObject)) {
        setIsProgrammaticChange(true)
        formRef.current = visibleFormData
        setPrevFormData({ ...visibleFormData.resultObject })
        setInitialFilled(true)
        
        // Сбрасываем флаг программного изменения после небольшой задержки
        setTimeout(() => setIsProgrammaticChange(false), 100)
      }
    }
  }, [visibleFormData, claimWorkFormData])

  const saveCard = useCallback(async (isNextStage = false) => {
    await saveResult(
      id,
      formRef.current,
      undefined,
      isNextStage
    )
  }, [id, saveResult])

  useEffect(() => {
    const redirectUrl = claimWorkFormData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [claimWorkFormData, navigate])

  const [isProgrammaticChange, setIsProgrammaticChange] = useState(false)
  const debounceSaveCase = useDebounce(saveCard, 1500)

  const onChangeForm = useCallback(async ({ formData }) => {
    if (isProgrammaticChange) {
      return
    }
    if (!formRef.current) {
      return
    }

    formRef.current = {
      ...formRef.current,
      resultObject: formData
    }

    const compareObjects = (obj1: any, obj2: any): string[] => {
      const diff: string[] = []
      for (const key in obj1) {
        if (obj1.hasOwnProperty(key)) {
          if (!isEqual(obj1[key], obj2[key])) {
            diff.push(key)
          }
        }
      }
      return diff
    }

    if (isEqual(prevFormData, formData)) {
      return
    }

    if (!prevFormData && !isEqual(prevFormData, formData)) {
      setPrevFormData(formData)
      return
    }

    try {
      debounceSaveCase()
    } finally {
      setPrevFormData(formData)
    }
  }, [prevFormData, debounceSaveCase, isProgrammaticChange])

  useEffect(() => {
    return () => {
      setPrevFormData(undefined)
    }
  }, [])

  if (claimWorkFormError && !claimWorkFormIsLoading && !claimWorkFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка.
        Попробуйте перезагрузить страницу или открыть карточку повторно.
      </HStack>
    )
  }

  return (
    <Form
        {...formItemLayout}
        layout='horizontal'
        form={form}
        scrollToFirstError={true}
        style={{ width: '100%' }}
      >
      {claimWorkFormError && !claimWorkFormIsLoading ? (
        <div className={'alertWrapper'}>
          <Alert
            message='При выполнении операции возникла ошибка:'
            showIcon
            type='error'
            description={claimWorkFormError}
          />
        </div>
      ) : null}
      {!claimWorkFormIsLoading ? (
        <VStack gap={'16'} max align={'stretch'} className={'claimWorkForm'}>
          {formRef.current && (
            <DefaultJsonForm
              formData={formRef.current.resultObject}
              schema={formRef.current.resultSchema}
              onChange={onChangeForm}
              orientation='horizontal'
            />
          )}
        </VStack>
      ) : (
        <Skeleton active />
      )}
    </Form>
  )
})

